/**
 * FinalFour - Final Four + Championship display.
 *
 * Dramatic layout with glowing championship game, large champion display,
 * and clear visual hierarchy between semifinal and final games.
 */

import BracketMatchup from './BracketMatchup';

function FinalFour({
  picks,
  entriesMap,
  predictions = {},
  onPickWinner,
  onAnalyze,
  onAskKnewton,
  onMascotBattle,
  onPremiumGate,
  isPremium = false,
  readOnly = false,
}) {
  const f4Picks = picks
    .filter(p => p.round_number === 5)
    .sort((a, b) => a.game_slot - b.game_slot);
  const champPick = picks.find(p => p.round_number === 6);

  const championId = champPick?.winner_entry_id;
  const champion = championId ? entriesMap[championId] : null;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Champion Display */}
      <div className="text-center mb-8">
        {champion ? (
          <div className="relative inline-block">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 via-yellow-400/20 to-amber-500/20 rounded-2xl blur-xl" />

            <div className="relative glass-card p-6 rounded-2xl border border-amber-500/30">
              <div className="text-[10px] text-amber-400 tracking-[0.2em] uppercase font-bold mb-3">
                National Champion
              </div>
              <div className="flex items-center justify-center gap-4">
                {/* Trophy */}
                <svg className="w-10 h-10 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 00-.584.859 6.753 6.753 0 006.138 5.6 6.73 6.73 0 002.743 1.346A6.707 6.707 0 019.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a.75.75 0 000 1.5h12.17a.75.75 0 000-1.5h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.707 6.707 0 01-1.112-3.173 6.73 6.73 0 002.743-1.347 6.753 6.753 0 006.139-5.6.75.75 0 00-.585-.858 47.077 47.077 0 00-3.07-.543V2.62a.75.75 0 00-.658-.744 49.22 49.22 0 00-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 00-.657.744z" />
                </svg>

                {/* Team info */}
                <div className="flex flex-col items-start">
                  {champion.team_logo_url ? (
                    <img src={champion.team_logo_url} alt="" className="w-14 h-14 object-contain mb-1" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center mb-1">
                      <span className="text-xl font-black text-white">{champion.seed}</span>
                    </div>
                  )}
                  <span className="text-xl font-black text-amber-400">
                    {champion.team_name || champion.team_abbreviation}
                  </span>
                  <span className="text-xs text-amber-400/60">
                    #{champion.seed} seed &middot; {champion.region}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="glass-card p-6 rounded-2xl border border-border/30 border-dashed inline-block">
            <div className="text-[10px] text-text-tertiary tracking-[0.2em] uppercase font-bold mb-3">
              National Champion
            </div>
            <div className="flex items-center justify-center gap-3">
              <svg className="w-10 h-10 text-text-tertiary/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.016 6.016 0 01-2.77.993M14.52 9.728a6.016 6.016 0 01-2.77.993" />
              </svg>
              <div className="text-left">
                <span className="text-sm text-text-tertiary block">Complete your bracket</span>
                <span className="text-[10px] text-text-tertiary/60">Pick all games to crown a champion</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Championship Game */}
      <div className="mb-8">
        <div className="text-center mb-3">
          <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-accent-purple">
            Championship Game
          </span>
        </div>
        <div className="flex justify-center">
          <div className="relative">
            {/* Glow behind championship matchup */}
            <div className="absolute -inset-2 bg-gradient-to-r from-accent-purple/10 to-accent-blue/10 rounded-xl blur-md" />
            <div className="relative">
              <BracketMatchup
                pick={champPick || null}
                entriesMap={entriesMap}
                prediction={predictions['6-1']}
                onPickWinner={onPickWinner}
                onAnalyze={onAnalyze}
                onAskKnewton={onAskKnewton}
                onMascotBattle={onMascotBattle}
                onPremiumGate={onPremiumGate}
                isPremium={isPremium}
                roundNumber={6}
                gameSlot={1}
                readOnly={readOnly}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Connector lines to Championship */}
      <div className="flex justify-center mb-4">
        <svg width="200" height="20" className="text-text-secondary">
          <line x1={40} y1={0} x2={40} y2={10} stroke="currentColor" strokeWidth={1.5} opacity={0.2} />
          <line x1={160} y1={0} x2={160} y2={10} stroke="currentColor" strokeWidth={1.5} opacity={0.2} />
          <line x1={40} y1={10} x2={160} y2={10} stroke="currentColor" strokeWidth={1.5} opacity={0.2} />
          <line x1={100} y1={10} x2={100} y2={20} stroke="currentColor" strokeWidth={1.5} opacity={0.2} />
        </svg>
      </div>

      {/* Final Four Games */}
      <div>
        <div className="text-center mb-3">
          <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-text-tertiary">
            Final Four
          </span>
        </div>
        <div className="flex justify-center gap-8">
          {f4Picks.length > 0 ? (
            f4Picks.map(pick => (
              <BracketMatchup
                key={`f4-${pick.game_slot}`}
                pick={pick}
                entriesMap={entriesMap}
                prediction={predictions[`5-${pick.game_slot}`]}
                onPickWinner={onPickWinner}
                onAnalyze={onAnalyze}
                onAskKnewton={onAskKnewton}
                onMascotBattle={onMascotBattle}
                onPremiumGate={onPremiumGate}
                isPremium={isPremium}
                roundNumber={5}
                gameSlot={pick.game_slot}
                readOnly={readOnly}
              />
            ))
          ) : (
            <>
              <BracketMatchup
                pick={null}
                entriesMap={entriesMap}
                roundNumber={5}
                gameSlot={1}
                readOnly={readOnly}
              />
              <BracketMatchup
                pick={null}
                entriesMap={entriesMap}
                roundNumber={5}
                gameSlot={2}
                readOnly={readOnly}
              />
            </>
          )}
        </div>
      </div>

      {/* Region labels under F4 games */}
      <div className="flex justify-center gap-8 mt-2">
        <div className="w-52 text-center text-[10px] text-text-tertiary">
          East vs South
        </div>
        <div className="w-52 text-center text-[10px] text-text-tertiary">
          West vs Midwest
        </div>
      </div>
    </div>
  );
}

export default FinalFour;
